import type { BoardFilters } from '@an-crm/shared';

/** Filter bar state: BoardFilters with the search box always present (possibly empty). */
export type FilterState = Omit<BoardFilters, 'q'> & { q: string };

export const EMPTY_FILTERS: FilterState = { q: '' };

export function activeFilterCount(f: FilterState): number {
  return Object.values(f).filter((v) => v !== undefined && String(v).trim() !== '').length;
}

/** The query sent to GET /pipeline/board: no empty values, trimmed search. */
export function toBoardFilters(f: FilterState): BoardFilters {
  const out: BoardFilters = {};
  if (f.icpId) out.icpId = f.icpId;
  if (f.serviceId) out.serviceId = f.serviceId;
  if (f.ownerId) out.ownerId = f.ownerId;
  if (f.rag) out.rag = f.rag;
  if (f.from) out.from = f.from;
  if (f.to) out.to = f.to;
  const q = f.q.trim();
  if (q) out.q = q;
  return out;
}

/** What the /pipeline URL may carry: filters, the weighted toggle and an open card (shareable links). */
export interface PipelineSearch extends BoardFilters {
  weighted?: boolean;
  open?: string;
}

const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isoDate = /^\d{4}-\d{2}-\d{2}$/;

/** Lenient: anything malformed in a hand-edited or old link is dropped, never an error page. */
export function parsePipelineSearch(raw: Record<string, unknown>): PipelineSearch {
  const str = (k: string) => (typeof raw[k] === 'string' ? (raw[k] as string) : undefined);
  const out: PipelineSearch = {};
  for (const key of ['icpId', 'serviceId', 'ownerId'] as const) {
    const v = str(key);
    if (v && uuid.test(v)) out[key] = v;
  }
  const open = str('open');
  if (open && /^[\w-]{1,64}$/.test(open)) out.open = open;
  const rag = str('rag');
  if (rag === 'green' || rag === 'amber' || rag === 'red') out.rag = rag;
  for (const key of ['from', 'to'] as const) {
    const v = str(key);
    if (v && isoDate.test(v)) out[key] = v;
  }
  const q = str('q')?.trim().slice(0, 100);
  if (q) out.q = q;
  if (raw.weighted === true || raw.weighted === 'true') out.weighted = true;
  return out;
}

/** URL search → filter bar state (the search text box is managed separately while typing). */
export function filtersFromSearch(s: PipelineSearch): FilterState {
  const { weighted: _w, open: _o, q, ...rest } = s;
  return { ...rest, q: q ?? '' };
}
